import { useLanguage } from "@/contexts/LanguageContext";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { HOW_IT_WORKS_STEPS } from "./constants";

export function HowItWorks() {
  const { t } = useLanguage();
  const { ref, isInView } = useInView();

  return (
    <section id="how-it-works" className="py-20 md:py-28">
      <PageContainer width="wide">
        <div
          ref={ref}
          className={cn(
            "transition-all duration-700",
            isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
          )}
        >
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <span className="block text-xs font-semibold tracking-[0.2em] uppercase text-brand mb-3">
              {t.landing.eyebrowProcess}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 md:mb-6">
              {t.landing.howItWorksTitle}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {t.landing.howItWorksSubtitle}
            </p>
          </div>

          {/* Steps grid — connector line sits behind on desktop. */}
          <div className="relative">
            <div
              aria-hidden
              className="hidden md:block absolute top-12 left-[15%] right-[15%] h-px bg-gradient-to-r from-brand/0 via-brand/40 to-brand/0"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
              {HOW_IT_WORKS_STEPS.map((step, idx) => {
                const Icon = step.icon;
                const title = t.howItWorks.imageGenerator[step.titleKey];
                const desc = t.howItWorks.imageGenerator[step.descKey];
                return (
                  <div
                    key={step.titleKey}
                    className={cn(
                      "group relative flex flex-col items-center text-center rounded-2xl border border-border bg-card p-6 md:p-8",
                      "transition-all duration-300 hover:border-brand/40 hover:shadow-lg hover:-translate-y-0.5",
                      isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
                    )}
                    style={{ transitionDelay: `${idx * 120}ms` }}
                  >
                    {/* Numbered icon disc — brand gradient, sits over the connector line. */}
                    <div className="relative">
                      <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-brand to-brand-secondary shadow-lg shadow-brand/30">
                        <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                      </div>
                      <span className="absolute -top-1 -end-1 flex items-center justify-center w-6 h-6 rounded-full bg-background border border-border text-xs font-bold text-foreground">
                        {idx + 1}
                      </span>
                    </div>
                    <h3 className="mt-5 md:mt-6 text-xl md:text-2xl font-bold text-foreground">
                      {title}
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                      {desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </PageContainer>
    </section>
  );
}
